/**
 * FarmMapService.js - Service pour la gestion des données cartographiques
 *
 * Gère les appels API pour :
 * - Localisation GPS des fermes
 * - Positions des capteurs
 * - Délimitations des secteurs
 * - Recherche de proximité
 */

import { auth } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

/**
 * Récupère le token d'authentification Firebase
 */
async function getAuthToken() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Utilisateur non authentifié');
  }
  return await user.getIdToken();
}

/**
 * Effectue une requête HTTP avec authentification
 */
async function fetchWithAuth(url, options = {}) {
  const token = await getAuthToken();

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `Erreur HTTP ${response.status}`);
  }

  return await response.json();
}

class FarmMapService {

  // =========================================================================
  // LOCALISATION DES FERMES
  // =========================================================================

  /**
   * Récupère les coordonnées GPS d'une ferme
   * @param {string} farmId - ID de la ferme
   * @returns {Promise<{latitude, longitude, altitude, locationSource, notes}>}
   */
  async getFarmLocation(farmId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/farms/${farmId}/location`
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la localisation:', error);
      throw error;
    }
  }

  /**
   * Définit ou met à jour les coordonnées GPS d'une ferme
   * @param {string} farmId - ID de la ferme
   * @param {Object} location - {latitude, longitude, altitude?, locationSource?, notes?}
   * @returns {Promise<Object>}
   */
  async setFarmLocation(farmId, location) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/farms/${farmId}/location`,
        {
          method: 'POST',
          body: JSON.stringify(location)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la localisation:', error);
      throw error;
    }
  }

  // =========================================================================
  // CAPTEURS
  // =========================================================================

  /**
   * Récupère tous les capteurs d'une ferme avec leurs positions GPS
   * @param {string} farmId - ID de la ferme
   * @returns {Promise<Array>} - Liste des capteurs avec GPS et dernière mesure
   */
  async getSensorsWithLocation(farmId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/farms/${farmId}/sensors`
      );
      return response.data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des capteurs:', error);
      throw error;
    }
  }

  /**
   * Crée un nouveau capteur
   * @param {Object} sensorData - {source_id, farmId, sectorId?, sensor_name?, gps_latitude?, gps_longitude?, metadata?}
   * @returns {Promise<Object>} - Capteur créé
   */
  async createSensor(sensorData) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/sensors`,
        {
          method: 'POST',
          body: JSON.stringify(sensorData)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du capteur:', error);
      if (error.message.includes('409')) {
        throw new Error('Un capteur avec ce numéro de série existe déjà');
      }
      throw error;
    }
  }

  /**
   * Définit ou met à jour la position GPS d'un capteur
   * @param {string} sourceId - ID du capteur
   * @param {Object} location - {farmId, sectorId?, latitude, longitude, altitude?, locationDescription?}
   * @returns {Promise<Object>}
   */
  async setSensorLocation(sourceId, location) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/sensors/${sourceId}/location`,
        {
          method: 'POST',
          body: JSON.stringify(location)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du capteur:', error);
      throw error;
    }
  }

  /**
   * Met à jour les informations d'un capteur
   * @param {string} sourceId - ID du capteur
   * @param {Object} updateData - {farmId, sensorName?, sensorType?, sectorId?, gpsLatitude?, gpsLongitude?, metadata?}
   * @returns {Promise<Object>}
   */
  async updateSensor(sourceId, updateData) {
    try {
      // Mapping camelCase → snake_case pour l'API PostgreSQL
      const apiData = {
        farm_id: updateData.farmId,
        sensor_name: updateData.sensorName,
        sensor_type: updateData.sensorType,
        sector_id: updateData.sectorId,
        gps_latitude: updateData.gpsLatitude,
        gps_longitude: updateData.gpsLongitude,
        metadata: updateData.metadata
      };

      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/sensors/${sourceId}`,
        {
          method: 'PUT',
          body: JSON.stringify(apiData)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du capteur:', error);
      throw error;
    }
  }

  /**
   * Trouve les capteurs à proximité d'un point GPS
   * @param {string} farmId - ID de la ferme
   * @param {number} latitude - Latitude du point
   * @param {number} longitude - Longitude du point
   * @param {number} radiusMeters - Rayon de recherche en mètres (défaut: 100)
   * @returns {Promise<Array>} - Capteurs à proximité avec distance
   */
  async findNearbySensors(farmId, latitude, longitude, radiusMeters = 100) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/farms/${farmId}/nearby-sensors?lat=${latitude}&lng=${longitude}&radius=${radiusMeters}`
      );
      return response.data || [];
    } catch (error) {
      console.error('Erreur lors de la recherche de capteurs à proximité:', error);
      throw error;
    }
  }

  // =========================================================================
  // SECTEURS
  // =========================================================================

  /**
   * Crée un nouveau secteur
   * @param {Object} sectorData - {sectorId, farmId, name?, compartmentId?, gpsLatitude?, gpsLongitude?, metadata?}
   * @returns {Promise<Object>} - Secteur créé (en camelCase)
   */
  async createSector(sectorData) {
    try {
      // Mapping camelCase → snake_case pour l'API PostgreSQL
      const apiData = {
        sector_id: sectorData.sectorId,
        farm_id: sectorData.farmId,
        name: sectorData.name,
        compartment_id: sectorData.compartmentId,
        gps_latitude: sectorData.gpsLatitude,
        gps_longitude: sectorData.gpsLongitude,
        metadata: sectorData.metadata
      };

      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/sectors`,
        {
          method: 'POST',
          body: JSON.stringify(apiData)
        }
      );

      // Mapping snake_case → camelCase pour le retour
      const sector = response.data;
      return {
        ...sector,
        sectorId: sector.sector_id || sector.sectorId,
        farmId: sector.farm_id || sector.farmId,
        compartmentId: sector.compartment_id || sector.compartmentId,
        gpsLatitude: sector.gps_latitude || sector.gpsLatitude,
        gpsLongitude: sector.gps_longitude || sector.gpsLongitude
      };
    } catch (error) {
      console.error('Erreur lors de la création du secteur:', error);
      if (error.message.includes('409')) {
        throw new Error('Un secteur avec cet ID existe déjà');
      }
      throw error;
    }
  }

  /**
   * Récupère les délimitations (polygones) de tous les secteurs d'une ferme
   * @param {string} farmId - ID de la ferme
   * @returns {Promise<Array>} - Liste des secteurs avec leurs polygones (en camelCase)
   */
  async getSectorBoundaries(farmId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/farms/${farmId}/sector-boundaries`
      );

      // Mapping snake_case → camelCase
      const boundaries = response.data || [];
      return boundaries.map(b => ({
        ...b,
        sectorId: b.sector_id || b.sectorId,
        boundaryPolygon: b.boundary_polygon || b.boundaryPolygon,
        areaSquareMeters: b.area_square_meters || b.areaSquareMeters
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des délimitations:', error);
      throw error;
    }
  }

  /**
   * Définit ou met à jour la délimitation (polygone) d'un secteur
   * @param {string} sectorId - ID du secteur
   * @param {Object} boundary - {coordinates: [[lng, lat], ...], area?: number}
   * @returns {Promise<Object>}
   */
  async setSectorBoundary(sectorId, boundary) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/sectors/${sectorId}/boundary`,
        {
          method: 'POST',
          body: JSON.stringify(boundary)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la délimitation:', error);
      throw error;
    }
  }

  /**
   * Met à jour les informations d'un secteur
   * @param {string} sectorId - ID du secteur
   * @param {Object} updateData - {name?, compartmentId?, gpsLatitude?, gpsLongitude?, metadata?}
   * @returns {Promise<Object>}
   */
  async updateSector(sectorId, updateData) {
    try {
      // Mapping camelCase → snake_case pour l'API PostgreSQL
      const apiData = {
        name: updateData.name,
        compartment_id: updateData.compartmentId,
        gps_latitude: updateData.gpsLatitude,
        gps_longitude: updateData.gpsLongitude,
        metadata: updateData.metadata
      };

      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/sectors/${sectorId}`,
        {
          method: 'PUT',
          body: JSON.stringify(apiData)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du secteur:', error);
      throw error;
    }
  }

  /**
   * Supprime un secteur
   * @param {string} sectorId - ID du secteur
   * @returns {Promise<Object>}
   */
  async deleteSector(sectorId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/farmroad/db/sectors/${sectorId}`,
        {
          method: 'DELETE'
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du secteur:', error);
      throw error;
    }
  }

  // =========================================================================
  // UTILITAIRES
  // =========================================================================

  /**
   * Convertit des coordonnées DMS (Degré Minute Seconde) en format décimal
   * Exemple: "35°05'17.0\"N 6°06'29.7\"W" → {lat: 35.088056, lng: -6.108250}
   * @param {string} dms - Coordonnées au format DMS
   * @returns {{lat: number, lng: number}}
   */
  parseDMSCoordinates(dms) {
    const regex = /(\d+)°(\d+)'([\d.]+)"([NSEW])/g;
    const matches = [...dms.matchAll(regex)];

    if (matches.length !== 2) {
      throw new Error('Format de coordonnées invalide');
    }

    const [latMatch, lngMatch] = matches;

    const parseDMS = (deg, min, sec, dir) => {
      let decimal = parseFloat(deg) + parseFloat(min) / 60 + parseFloat(sec) / 3600;
      if (dir === 'S' || dir === 'W') {
        decimal = -decimal;
      }
      return decimal;
    };

    return {
      lat: parseDMS(latMatch[1], latMatch[2], latMatch[3], latMatch[4]),
      lng: parseDMS(lngMatch[1], lngMatch[2], lngMatch[3], lngMatch[4])
    };
  }

  /**
   * Calcule la distance entre deux points GPS (formule de Haversine)
   * @param {number} lat1 - Latitude du premier point
   * @param {number} lng1 - Longitude du premier point
   * @param {number} lat2 - Latitude du second point
   * @param {number} lng2 - Longitude du second point
   * @returns {number} - Distance en mètres
   */
  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000; // Rayon de la Terre en mètres
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Calcule la surface d'un polygone en mètres carrés
   * @param {Array<[number, number]>} coordinates - Tableau de coordonnées [lng, lat]
   * @returns {number} - Surface en m²
   */
  calculatePolygonArea(coordinates) {
    if (!coordinates || coordinates.length < 3) {
      return 0;
    }

    const R = 6371000; // Rayon de la Terre en mètres
    let area = 0;

    for (let i = 0; i < coordinates.length - 1; i++) {
      const [lng1, lat1] = coordinates[i];
      const [lng2, lat2] = coordinates[i + 1];

      area += (lng2 - lng1) * Math.PI / 180 *
              (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180));
    }

    area = Math.abs(area * R * R / 2);
    return area;
  }
}

// Export d'une instance singleton
export default new FarmMapService();
